(ns hai.notes.data
  "Note mutations and optimistic edit state. Every mutation returns the fresh
   note (the shared editor shape) and stores it at the entity ident the detail
   page reads; the wall re-queries its rows and facet counts when mounted,
   since a paged, filtered collection can't be reconciled from one note."
  (:require
   [clojure.string :as str]
   [hai.entity.staging :as staging]
   [hai.notes.mutations :as mutations]
   [hai.ui.rf-sugar :refer [defevent-fx defsub]]))

(def table-id
  "Collection id of the notes wall. The mutations re-resolve it when mounted."
  ::table)

(defn- note-ident
  [note-uuid]
  [:note/uuid (uuid (str note-uuid))])

(defn- wall-refresh-fx
  [db]
  (cond-> []
    (get-in db [:collections table-id])
    (conj [:dispatch [:table/resolve table-id]]
          [:dispatch [::load-facets]])))

(defevent-fx ::delete [{:keys [db]} note-uuid]
  ;; The caller navigates away immediately; drop the wall's row now too
  ;; (mount-guarded inside :table/remove-row), and resurrect it with a toast if
  ;; the delete fails.
  {:fx [[:dispatch [:table/remove-row table-id :note/uuid note-uuid]]]
   :resolve (assoc (mutations/delete-resolve note-uuid)
                   :on-success-fx (conj (wall-refresh-fx db)
                                        [:dispatch [:entity/clear {:note/uuid note-uuid}]])
                   :on-error-fx (conj (wall-refresh-fx db)
                                      [:dispatch [:toast/error {:text "Could not delete note. Try again."}]]))})

(defevent-fx ::save [{:keys [db]} {note-uuid :note/uuid note-text :note/text}]
  {:resolve (assoc (mutations/upsert-resolve {:note/uuid note-uuid :note/text note-text})
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db))})

;; Adds and removals are optimistic through [[hai.entity.staging]]: the chip
;; shows (or hides) immediately while the note's cached copy lags, the success
;; re-resolve — which then carries the change — retires the staged edit, and an
;; error retires it with a toast.
(defn- staged-add
  "The `:db` staging and success/error fx every optimistic add shares."
  [db {note-uuid :note/uuid :keys [family chip error-text resolve]}]
  {:db (staging/stage db note-uuid family chip)
   :resolve (assoc resolve
                   :store-in (note-ident note-uuid)
                   :on-success-fx (conj (wall-refresh-fx db)
                                        [:dispatch [::staging/unstage note-uuid family chip]])
                   :on-error-fx [[:dispatch [::staging/add-failed note-uuid family chip error-text]]])})

(defn- marked-removal
  "The `:db` removal mark and success/error fx every optimistic removal
   shares."
  [db {note-uuid :note/uuid :keys [family id error-text resolve]}]
  {:db (staging/mark-removal db note-uuid family id)
   :resolve (assoc resolve
                   :store-in (note-ident note-uuid)
                   :on-success-fx (conj (wall-refresh-fx db)
                                        [:dispatch [::staging/unmark-removal note-uuid family id]])
                   :on-error-fx [[:dispatch [::staging/remove-failed note-uuid family id error-text]]])})

(defevent-fx ::add-link [{:keys [db]} {note-uuid :note/uuid link-url :url}]
  ;; Re-adding a URL lifts any leftover optimistic-removal mark for it.
  (-> db
      (staging/unmark-removal note-uuid :links link-url)
      (staged-add {:note/uuid note-uuid
                   :family :links
                   :chip link-url
                   :error-text "Could not add link. Try again."
                   :resolve (mutations/upsert-resolve {:note/uuid note-uuid
                                                       :activity/urls [link-url]})})))

(defevent-fx ::remove-link [{:keys [db]} {note-uuid :note/uuid link-url :url}]
  (marked-removal db {:note/uuid note-uuid
                      :family :links
                      :id link-url
                      :error-text "Could not remove link. Try again."
                      :resolve (mutations/remove-link-resolve note-uuid link-url)}))

;; Tag detach goes through note/upsert (`:note/untag-uuids`) rather than a
;; dedicated mutation.
(defevent-fx ::remove-tag [{:keys [db]} {note-uuid :note/uuid tag-uuid :group/uuid}]
  (marked-removal db {:note/uuid note-uuid
                      :family :tags
                      :id tag-uuid
                      :error-text "Could not remove tag. Try again."
                      :resolve (mutations/upsert-resolve {:note/uuid note-uuid
                                                          :note/untag-uuids [tag-uuid]})}))

(defevent-fx ::add-tag [{:keys [db]} {note-uuid :note/uuid :keys [tag]}]
  ;; Re-adding a tag lifts any leftover optimistic-removal mark for it, so it
  ;; shows again once the re-resolve lands (mirrors ::add-link).
  (let [tag-uuid (:group/uuid tag)]
    (-> db
        (cond-> tag-uuid (staging/unmark-removal note-uuid :tags tag-uuid))
        (staged-add {:note/uuid note-uuid
                     :family :tags
                     :chip (staging/tag-chip tag)
                     :error-text "Could not add tag. Try again."
                     :resolve (mutations/upsert-resolve
                               (cond-> {:note/uuid note-uuid}
                                 tag-uuid (assoc :note/tag-uuids [tag-uuid])
                                 (nil? tag-uuid) (assoc :note/tag-names [(:tag/name tag)])))}))))

(defevent-fx ::remove-contact [{:keys [db]} {note-uuid :note/uuid contact-uuid :contact/uuid}]
  (marked-removal db {:note/uuid note-uuid
                      :family :contacts
                      :id contact-uuid
                      :error-text "Could not remove contact. Try again."
                      :resolve (mutations/remove-contact-resolve note-uuid contact-uuid)}))

(defevent-fx ::add-contact [{:keys [db]} {note-uuid :note/uuid :keys [contact]}]
  ;; Re-adding a contact lifts any leftover optimistic-removal mark for it,
  ;; so it shows again once the re-resolve lands (mirrors ::add-link).
  (let [contact-uuid (:contact/uuid contact)]
    (-> db
        (staging/unmark-removal note-uuid :contacts contact-uuid)
        (staged-add {:note/uuid note-uuid
                     :family :contacts
                     :chip (staging/contact-chip contact)
                     :error-text "Could not add contact. Try again."
                     :resolve (mutations/upsert-resolve {:note/uuid note-uuid
                                                         :note/contact-uuids [contact-uuid]})}))))

;; Linked notes and tasks follow the contact pattern exactly. Note links are
;; one-way, so only the note being edited changes.
(defevent-fx ::remove-note-link [{:keys [db]} {note-uuid :note/uuid linked-uuid :note/linked-uuid}]
  (marked-removal db {:note/uuid note-uuid
                      :family :notes
                      :id linked-uuid
                      :error-text "Could not remove linked note. Try again."
                      :resolve (mutations/remove-note-resolve note-uuid linked-uuid)}))

(defevent-fx ::add-note-link [{:keys [db]} {note-uuid :note/uuid :keys [note]}]
  (let [linked-uuid (:note/uuid note)]
    (-> db
        (staging/unmark-removal note-uuid :notes linked-uuid)
        (staged-add {:note/uuid note-uuid
                     :family :notes
                     :chip (staging/note-chip note)
                     :error-text "Could not add linked note. Try again."
                     :resolve (mutations/upsert-resolve {:note/uuid note-uuid
                                                         :note/note-uuids [linked-uuid]})}))))

(defevent-fx ::remove-task [{:keys [db]} {note-uuid :note/uuid task-uuid :task/uuid}]
  (marked-removal db {:note/uuid note-uuid
                      :family :tasks
                      :id task-uuid
                      :error-text "Could not remove linked task. Try again."
                      :resolve (mutations/remove-task-resolve note-uuid task-uuid)}))

(defevent-fx ::add-task [{:keys [db]} {note-uuid :note/uuid :keys [task]}]
  (let [task-uuid (:task/uuid task)]
    (-> db
        (staging/unmark-removal note-uuid :tasks task-uuid)
        (staged-add {:note/uuid note-uuid
                     :family :tasks
                     :chip (staging/task-chip task)
                     :error-text "Could not add linked task. Try again."
                     :resolve (mutations/upsert-resolve {:note/uuid note-uuid
                                                         :note/task-uuids [task-uuid]})}))))

(defsub ::saving? [db note-uuid]
  (contains? (get db ::saving #{}) note-uuid))

(defsub ::saved-at [db note-uuid]
  (get-in db [::saved-at note-uuid]))

(def ^:private facets-eql
  [:facet/total
   :facet/no-web-page-count
   :facet/not-shared-count
   :facet/untagged-count
   :facet/no-contact-count
   {:facet/tags [:tag/name :facet/count]}
   {:facet/contacts [:contact/uuid :contact/full-name :facet/count]}
   {:facet/domains [:domain/name :facet/count
                    {:domain/urls [:url/value :url/label :facet/count]}]}
   {:facet/shared [:user/uuid :facet/label :facet/count]}])

(defn- live-filters
  "The wall's active filter values, read from the filters SUB-form's fields —
   not the root form's `:table/filters` map. A sub-field write lands in the
   sub-form's field state synchronously but reaches the root map only through
   a chain of queued events, so a facet-click handler dispatching
   [[::load-facets]] right after `set-value!` would read a stale root."
  [db]
  (into {}
        (keep (fn [[attr {current-value :form.field/current-value}]]
                (when (some? current-value)
                  [attr current-value])))
        (get-in db [:forms [table-id :table/filters] :form/fields])))

;; Facet option lists + SMART counts for the filter sidebar
;; (`hai.pathom.notes/user-note-facets`): each dimension's counts respect the
;; wall's live search + the OTHER dimensions' filters, sent as query params.
;; Loaded on sidebar mount, after note mutations, and after every filter or
;; (debounced) search change.
(defevent-fx ::load-facets
  [{:keys [db]} _]
  (let [active (live-filters db)
        search (get-in db [:forms table-id :form/fields
                           :table/search :form.field/current-value])
        params (cond-> {}
                 (seq active) (assoc :filters active)
                 (some-> search str/trim not-empty) (assoc :search search))]
    {:resolve {:query        [(if (seq params)
                                (list {:user/note-facets facets-eql} params)
                                {:user/note-facets facets-eql})]
               :transform-fn :user/note-facets
               :store-in     [::facets]}}))

;; Search keystrokes funnel through this so the counts re-query once typing
;; pauses — the same cadence as the table's own debounced resolve.
(defevent-fx ::load-facets-debounced
  [_ _]
  {:debounce/dispatch {:id ::load-facets
                       :event [::load-facets]
                       :duration 500}})

(defsub ::facets [db]
  (get db ::facets))
